import { uiText, useUiLanguage } from '@/i18n/interface';
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ColorThemeProvider } from "./contexts/ColorThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { useTrialStatus } from "./hooks/useTrialStatus";
import Index from "./pages/Index";
import Login from "./pages/Login";
import StudentRegistration from "./pages/StudentRegistration";
import StudentDashboard from "./pages/StudentDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import SuperUserDashboard from "./pages/SuperUserDashboard";
import TrainingSchedule from "./pages/TrainingSchedule";
import BeltRequirements from "./pages/BeltRequirements";
import ParticipationHistory from "./pages/ParticipationHistory";
import Disclaimer from "./pages/Disclaimer";
import SecurityManagement from "./pages/SecurityManagement";
import NotFound from "./pages/NotFound";
import TrialExpiredScreen from "./components/TrialExpiredScreen";
import Subscription from "./pages/Subscription";
import ConfirmSignup from "./pages/ConfirmSignup";
import ResetPassword from "./pages/ResetPassword";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const AppContent = () => {
  useUiLanguage();
  const { isAuthenticated, profile, user } = useAuth();
  const location = useLocation();
  const { isTrialExpired, loading: trialLoading } = useTrialStatus();
  const [initialLoading, setInitialLoading] = useState(true);

  // Handle initial loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Confirmation must finish even while the new user's profile is loading.
  if (location.pathname === '/confirm-signup') return <ConfirmSignup />;

  // Show loading state during initial auth check or when user is authenticated but profile is loading
  if (initialLoading || (isAuthenticated && user && !profile && !trialLoading)) {
    return (
      <div className="wh-app min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground text-lg">{uiText("Loading your dashboard...")}</p>
        </div>
      </div>
    );
  }

  // Show trial expired screen for expired trial users
  if (isAuthenticated && profile?.role === 'admin' && isTrialExpired && !trialLoading) {
    return <TrialExpiredScreen />;
  }


  return (
    <Routes>
      <Route path="/" element={isAuthenticated ? (
        profile?.role === 'student' ? <StudentDashboard /> :
        profile?.role === 'admin' ? <AdminDashboard /> :
        profile?.role === 'super_user' ? <SuperUserDashboard /> :
        <Login />
      ) : <Login />} />
      <Route path="/index" element={<Index />} />
      <Route path="/login" element={!isAuthenticated ? <Login /> : (
        profile?.role === 'student' ? <StudentDashboard /> :
        profile?.role === 'admin' ? <AdminDashboard /> :
        profile?.role === 'super_user' ? <SuperUserDashboard /> :
        <Login />
      )} />
      <Route path="/register" element={!isAuthenticated ? <StudentRegistration /> : (
        profile?.role === 'student' ? <StudentDashboard /> :
        profile?.role === 'admin' ? <AdminDashboard /> :
        profile?.role === 'super_user' ? <SuperUserDashboard /> :
        <Login />
      )} />
      <Route path="/student" element={isAuthenticated && profile?.role === 'student' ? <StudentDashboard /> : <Login />} />
      <Route path="/admin" element={isAuthenticated && profile?.role === 'admin' ? <AdminDashboard /> : <Login />} />
      <Route path="/admin-dashboard" element={isAuthenticated && profile?.role === 'admin' ? <AdminDashboard /> : <Login />} />
      <Route path="/super-user" element={isAuthenticated && profile?.role === 'super_user' ? <SuperUserDashboard /> : <Login />} />
      <Route path="/schedule" element={isAuthenticated ? <TrainingSchedule /> : <Login />} />
      <Route path="/belt-requirements" element={isAuthenticated ? <BeltRequirements /> : <Login />} />

      <Route path="/participation-history" element={isAuthenticated ? <ParticipationHistory /> : <Login />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/security" element={isAuthenticated && profile?.role === 'admin' ? <SecurityManagement /> : <Login />} />
      <Route path="/subscription" element={<Subscription />} />
      <Route path="/confirm-signup" element={<ConfirmSignup />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () =>  { useUiLanguage(); return (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <LanguageProvider>
          <ColorThemeProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AppContent />
            </BrowserRouter>
          </TooltipProvider>
          </ColorThemeProvider>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
); };

export default App;